import React from "react";
import { Box, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import MapPopupShell from "./MapPopupShell";
import { FeatureAttributes } from "../../../store/featureInfoStore";
import { PopupCoords } from "../../../store/popupStore";
import { formatFieldName } from "../../../utilities/attributeRendering";
import { formatAttributeValue, listFeatureAttributes, pickFeatureTitle, splitMultiValue } from "../../../utilities/featureInfo";

// Attributes of a clicked vector tile feature (QGIS "identify"). A tile feature is a
// shape inside a PMTiles upload rather than an API entity, so there is nothing to
// fetch — everything shown here comes from the click event itself.

// Same slate family as the entity popup (DataItemDetails popupMode), including the
// popup tip colour in MapComponent.css. No media block: spatial features have none.
const POPUP_SURFACE = "#475467";
const POPUP_TEXT = "#FCFCFD";
const POPUP_MUTED_TEXT = "#D0D5DD";
const POPUP_WIDTH = "20rem";

interface Props {
  open: boolean;
  coords: PopupCoords | null;
  /** Features under the click point, topmost first. */
  features: FeatureAttributes[];
  activeIndex: number;
  /** Dataset name, shown as the muted subtitle under the feature title. */
  sourceName: string | null;
  onStep: (delta: number) => void;
  onClose: () => void;
}

const FeatureInfoPopup: React.FC<Props> = ({ open, coords, features, activeIndex, sourceName, onStep, onClose }) => {
  const feature = features[activeIndex];

  if (!open || !coords || !feature) return null;

  const layerName = sourceName || "Feature";
  const { key: titleKey, title } = pickFeatureTitle(feature, layerName);
  const attributes = listFeatureAttributes(feature, titleKey);
  const hasMultiple = features.length > 1;
  const showLayerSubtitle = Boolean(sourceName && title !== sourceName);

  return (
    <MapPopupShell popupKey={`feature-info-${activeIndex}`} coordinates={[coords.lng, coords.lat]} onClose={onClose}>
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          width: POPUP_WIDTH,
          backgroundColor: POPUP_SURFACE,
          color: POPUP_TEXT,
          borderRadius: "0.5rem",
          overflow: "hidden"
        }}
      >
        <IconButton
          size='small'
          aria-label='Close feature info'
          onClick={onClose}
          sx={{
            top: 8,
            right: 8,
            background: "#FCFCFD",
            width: 32,
            height: 32,
            zIndex: 10,
            position: "absolute",
            color: "#667085",
            padding: "0.625rem",
            opacity: 0.6,
            borderRadius: "0.25rem",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "& .MuiSvgIcon-root": {
              fontSize: "1.5rem",
              color: "#667085"
            },
            "&:hover": {
              background: "rgba(255, 255, 255, 0.95)",
              opacity: 1,
              boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)"
            }
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            pt: "2.75rem",
            px: "0.75rem",
            pb: "1rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem",
            maxHeight: "min(60vh, 560px)",
            overflowY: "auto"
          }}
        >
          <Box
            sx={{
              "&::after": {
                content: '""',
                display: "table",
                clear: "both"
              }
            }}
          >
            {hasMultiple && (
              <Box
                sx={{
                  float: "right",
                  display: "flex",
                  alignItems: "center",
                  ml: 0.75,
                  mb: 0.25
                }}
              >
                <IconButton size='small' aria-label='Previous feature' onClick={() => onStep(-1)} sx={{ color: POPUP_TEXT }}>
                  <ChevronLeftIcon fontSize='small' />
                </IconButton>
                <Typography variant='caption' sx={{ whiteSpace: "nowrap", color: POPUP_MUTED_TEXT }}>
                  {activeIndex + 1} of {features.length}
                </Typography>
                <IconButton size='small' aria-label='Next feature' onClick={() => onStep(1)} sx={{ color: POPUP_TEXT }}>
                  <ChevronRightIcon fontSize='small' />
                </IconButton>
              </Box>
            )}

            <Tooltip title={title}>
              <Typography
                variant='h4'
                component='div'
                sx={{
                  m: 0,
                  mb: "0.375rem",
                  color: "#FCFCFD",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  lineHeight: 1.35,
                  letterSpacing: "0.0175rem",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  overflowWrap: "anywhere"
                }}
              >
                {title}
              </Typography>
            </Tooltip>

            {showLayerSubtitle && (
              <Typography
                variant='body2'
                sx={{
                  color: POPUP_MUTED_TEXT,
                  lineHeight: 1.5,
                  fontSize: "0.8125rem",
                  overflowWrap: "anywhere"
                }}
              >
                {sourceName}
              </Typography>
            )}
          </Box>

          {attributes.length > 0 && (
            <Box>
              {attributes.map(([key, value]) => {
                const multiValue = splitMultiValue(value);

                return (
                  <Box key={key} sx={{ display: "flex", flexDirection: "column", gap: 0.25, py: 0.5 }}>
                    {/* Labels are humanised for reading; the raw column name stays in the
                        tooltip so it can still be matched against the source data. */}
                    <Typography variant='caption' title={key} sx={{ color: POPUP_MUTED_TEXT, overflowWrap: "anywhere" }}>
                      {formatFieldName(key).trim()}
                    </Typography>
                    <Typography variant='body2' component='div' sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                      {multiValue
                        ? multiValue.map((part) => (
                            <Box key={part} component='div'>
                              {part}
                            </Box>
                          ))
                        : formatAttributeValue(value)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Paper>
    </MapPopupShell>
  );
};

export default React.memo(FeatureInfoPopup);
